import { Component } from '@theme/component';
import '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';

const SHEET_QUERY = '(max-width: 1199px), (hover: none), (pointer: coarse)';
const DESKTOP_HOVER_QUERY = '(min-width: 1200px) and (hover: hover) and (pointer: fine)';
const HOVER_CLOSE_DELAY = 1000;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Only one Room & Look product panel may be active across the page. */
let activeInstance;

function getMediaQuery(query) {
  if (typeof window.matchMedia === 'function') return window.matchMedia(query);

  return {
    matches: false,
    addEventListener() {},
    removeEventListener() {},
    addListener() {},
    removeListener() {},
  };
}

function escapeSelector(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);

  return String(value).replace(/([^\w-])/g, '\\$1');
}

function getRaf() {
  return typeof window.requestAnimationFrame === 'function'
    ? window.requestAnimationFrame.bind(window)
    : (callback) => window.setTimeout(callback, 0);
}

/**
 * Owns Room & Look hotspot panels without taking ownership of collection links.
 *
 * Desktop uses a non-modal dialog positioned against its Look tile. Touch and
 * narrow viewports use native modal semantics with the section's bottom-sheet CSS.
 */
export class UlifRoomLookMosaic extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {AbortController | undefined} */
  #openController;

  /** @type {HTMLButtonElement | undefined} */
  #activeTrigger;

  /** @type {HTMLDialogElement | undefined} */
  #activeDialog;

  /** @type {MediaQueryList | undefined} */
  #sheetMedia;

  /** @type {MediaQueryList | undefined} */
  #hoverMedia;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #resizeFrame;

  /** @type {number | undefined} */
  #hoverCloseTimer;

  /** @type {Promise<void> | undefined} */
  #closePromise;

  /** @type {WeakSet<HTMLButtonElement>} */
  #boundHotspots = new WeakSet();

  /** @type {HTMLButtonElement | undefined} */
  #hoveredHotspot;

  /** @type {boolean} */
  #dialogHovered = false;

  /** @type {boolean} */
  #focusWithinActiveRegion = false;

  /** @type {boolean} */
  #suppressNativeDialogFocus = false;

  /** @type {boolean} */
  #legacyMediaListener = false;

  /** @type {boolean} */
  #legacyHoverMediaListener = false;

  /** @type {'closed' | 'hover' | 'activation'} */
  #openIntent = 'closed';

  /** @type {'hover' | 'click' | 'keyboard' | 'touch' | undefined} */
  #interactionSource;

  /** @type {boolean} */
  #suppressNativeClose = false;

  /** @type {boolean} */
  #connected = false;

  connectedCallback() {
    this.#unbindMediaQuery();
    this.#forceClose();
    this.#controller?.abort();

    super.connectedCallback();

    this.#connected = true;
    this.#controller = new AbortController();
    this.#boundHotspots = new WeakSet();
    this.#sheetMedia = getMediaQuery(SHEET_QUERY);
    this.#hoverMedia = getMediaQuery(DESKTOP_HOVER_QUERY);
    const signal = this.#controller.signal;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    document.addEventListener('ulif:room-look-close-others', this.#handleOtherInstanceClose, { signal });

    if (typeof this.#sheetMedia.addEventListener === 'function') {
      this.#sheetMedia.addEventListener('change', this.#handleDisplayModeChange, { signal });
    } else if (typeof this.#sheetMedia.addListener === 'function') {
      this.#sheetMedia.addListener(this.#handleDisplayModeChange);
      this.#legacyMediaListener = true;
    }

    if (typeof this.#hoverMedia.addEventListener === 'function') {
      this.#hoverMedia.addEventListener('change', this.#handleDisplayModeChange, { signal });
    } else if (typeof this.#hoverMedia.addListener === 'function') {
      this.#hoverMedia.addListener(this.#handleDisplayModeChange);
      this.#legacyHoverMediaListener = true;
    }

    this.#bindHotspotHoverListeners(signal);
  }

  updatedCallback() {
    super.updatedCallback();

    // Section Rendering API updates can replace a dialog while it is open.
    if (this.#connected && this.#activeDialog) void this.close({ restoreFocus: false });
    if (this.#connected && this.#controller) {
      this.#bindHotspotHoverListeners(this.#controller.signal);
    }
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#forceClose();
    this.#unbindMediaQuery();
    this.#controller?.abort();
    this.#controller = undefined;
    super.disconnectedCallback();
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const closeButton = target.closest('[data-room-look-close]');
    if (closeButton && this.contains(closeButton)) {
      event.preventDefault();
      void this.close();
      return;
    }

    const hotspot = target.closest('[data-room-look-hotspot]');
    if (hotspot && this.contains(hotspot)) {
      if (
        event instanceof MouseEvent &&
        (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }

      event.preventDefault();
      const interactionSource = this.#getActivationSource(event);
      void this.open(hotspot, {
        source: 'activation',
        focusPanel: true,
        interactionSource,
      });
      return;
    }

    const productLink = target.closest('[data-room-look-product-link]');
    if (productLink && this.contains(productLink)) {
      this.#publish('room_look_product_click', productLink, {
        interactionSource: this.#interactionSource,
      });
      return;
    }

    const roomLink = target.closest('[data-room-look-room-link]');
    if (roomLink && this.contains(roomLink)) {
      this.#publish('room_look_room_click', roomLink, {
        interactionSource: this.#getActivationSource(event),
      });
    }
  };

  #handleHotspotMouseEnter = (event) => {
    if (!this.#hoverMedia?.matches) return;

    const hotspot = event.currentTarget;
    if (!(hotspot instanceof HTMLButtonElement) || !this.contains(hotspot)) return;

    this.#hoveredHotspot = hotspot;
    this.#cancelHoverClose();

    // A click-pinned panel owns the interaction until an explicit activation changes it.
    if (this.#openIntent === 'activation' && hotspot !== this.#activeTrigger) return;

    void this.open(hotspot, {
      source: 'hover',
      focusPanel: false,
      interactionSource: 'hover',
    });
  };

  #handleHotspotMouseLeave = (event) => {
    const hotspot = event.currentTarget;
    if (!(hotspot instanceof HTMLButtonElement)) return;
    if (this.#hoveredHotspot === hotspot) this.#hoveredHotspot = undefined;
    if (!this.#hoverMedia?.matches || this.#openIntent !== 'hover') return;

    const next = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    if (next && this.#activeDialog?.contains(next)) {
      this.#cancelHoverClose();
      return;
    }

    if (hotspot === this.#activeTrigger) this.#scheduleHoverClose();
  };

  #handleDialogMouseEnter = (event) => {
    if (!this.#hoverMedia?.matches || event.currentTarget !== this.#activeDialog) return;

    this.#dialogHovered = true;
    this.#cancelHoverClose();
  };

  #handleDialogMouseLeave = (event) => {
    if (event.currentTarget !== this.#activeDialog) return;

    this.#dialogHovered = false;
    if (!this.#hoverMedia?.matches || this.#openIntent !== 'hover') return;

    const next = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    if (next && this.#activeTrigger?.contains(next)) {
      this.#cancelHoverClose();
      return;
    }

    this.#scheduleHoverClose();
  };

  #handleFocusIn = (event) => {
    if (this.#openIntent !== 'hover') return;

    const target = event.target instanceof Node ? event.target : null;
    if (!target || !this.#isWithinActiveRegion(target)) return;
    if (this.#suppressNativeDialogFocus) return;

    this.#focusWithinActiveRegion = true;
    this.#cancelHoverClose();
  };

  #handleFocusOut = (event) => {
    if (!this.#hoverMedia?.matches || this.#openIntent !== 'hover') return;

    const target = event.target instanceof Node ? event.target : null;
    if (!target || !this.#isWithinActiveRegion(target)) return;

    const next = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    if (next && this.#isWithinActiveRegion(next)) {
      this.#focusWithinActiveRegion = true;
      this.#cancelHoverClose();
      return;
    }

    this.#focusWithinActiveRegion = false;
    this.#scheduleHoverClose();
  };

  #handleKeydown = (event) => {
    if (!(event instanceof KeyboardEvent) || !this.#activeDialog) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      void this.close();
      return;
    }

    if (
      this.#activeDialog.dataset.displayMode !== 'sheet' ||
      event.key !== 'Tab' ||
      !(event.target instanceof Node) ||
      !this.#activeDialog.contains(event.target)
    ) {
      return;
    }
    this.#trapFocus(event, this.#activeDialog);
  };

  #handleDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#activeDialog) return;
    if (this.#activeDialog.dataset.displayMode === 'sheet') return;

    event.preventDefault();
    void this.close({ restoreFocus: this.#shouldRestoreFocus() });
  };

  #handleOutsidePointer = (event) => {
    const dialog = this.#activeDialog;
    if (!dialog || dialog.dataset.displayMode !== 'popover') return;

    const target = event.target;
    if (target instanceof Node && dialog.contains(target)) return;
    if (target instanceof Node && this.#activeTrigger?.contains(target)) return;

    void this.close({ restoreFocus: this.#openIntent !== 'hover' });
  };

  #handleDialogPointerDown = (event) => {
    const dialog = this.#activeDialog;
    if (!dialog || dialog.dataset.displayMode !== 'sheet' || event.target !== dialog) return;

    const rect = dialog.getBoundingClientRect();
    const outside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;

    if (outside) void this.close();
  };

  #handleDialogCancel = (event) => {
    event.preventDefault();
    void this.close();
  };

  #handleViewportResize = () => {
    this.#schedulePopoverPosition();
  };

  #handleNativeClose = () => {
    if (this.#suppressNativeClose || !this.#activeDialog) return;

    const trigger = this.#activeTrigger;
    const dialog = this.#activeDialog;
    this.#clearDialogState(dialog, trigger, { restoreFocus: this.#shouldRestoreFocus() });
  };

  #handleDisplayModeChange = () => {
    this.#cancelHoverClose();
    if (this.#activeDialog) void this.close({ restoreFocus: false });
  };

  #handleOtherInstanceClose = (event) => {
    if (event.detail?.source === this || !this.#activeDialog) return;
    void this.close({ restoreFocus: false });
  };

  #handleBlockSelect = (event) => {
    const eventTarget = event.target instanceof Element ? event.target : null;
    const targetBlock = eventTarget?.closest('[data-block-id]');
    const detail = event.detail ?? {};
    const blockId = targetBlock?.getAttribute('data-block-id') || detail.blockId || detail.block?.id;
    if (!blockId) return;

    const localTarget = targetBlock && this.contains(targetBlock) ? targetBlock : null;
    const selector = '[data-block-id="' + escapeSelector(String(blockId)) + '"]';
    const tile = localTarget || this.querySelector(selector);
    if (!(tile instanceof HTMLElement)) return;

    this.querySelectorAll('[data-editor-selected]').forEach((node) => {
      node.removeAttribute('data-editor-selected');
    });
    tile.setAttribute('data-editor-selected', '');

    if (this.#activeDialog) void this.close({ restoreFocus: false });
    tile.scrollIntoView({
      block: 'nearest',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  /**
   * Open the product panel belonging to a hotspot.
   *
   * @param {Element} trigger
   * @param {{
   *   source?: 'hover' | 'activation',
   *   focusPanel?: boolean,
   *   interactionSource?: 'hover' | 'click' | 'keyboard' | 'touch'
   * }} options
   * @returns {Promise<void>}
   */
  async open(
    trigger,
    {
      source = 'activation',
      focusPanel = source !== 'hover',
      interactionSource = source === 'hover' ? 'hover' : 'click',
    } = {}
  ) {
    if (!(trigger instanceof HTMLButtonElement) || !this.contains(trigger)) return;

    const dialogId = trigger.getAttribute('aria-controls');
    const dialog = dialogId
      ? this.querySelector('#' + escapeSelector(dialogId))
      : null;
    if (!(dialog instanceof HTMLDialogElement) || !this.contains(dialog)) return;

    const shell = dialog.closest('dialog-component');
    if (!(shell instanceof HTMLElement)) return;
    const focusBeforeOpen = document.activeElement;

    if (trigger === this.#activeTrigger) {
      this.#cancelHoverClose();
      if (source === 'activation') {
        if (this.#openIntent === 'hover') {
          this.#openIntent = 'activation';
          this.#interactionSource = interactionSource;
          if (focusPanel && dialog.open) this.#focusDialog(dialog);
          return;
        }

        await this.close();
      }
      return;
    }

    // Hover cannot displace a panel the visitor deliberately pinned by activation.
    if (source === 'hover' && this.#openIntent === 'activation') return;

    this.#cancelHoverClose();

    if (activeInstance && activeInstance !== this) {
      await activeInstance.close({ restoreFocus: false });
    }

    document.dispatchEvent(
      new CustomEvent('ulif:room-look-close-others', {
        detail: { source: this },
      })
    );
    await this.close({ restoreFocus: false });

    if (!this.#connected || !this.isConnected) return;

    this.#activeTrigger = trigger;
    this.#activeDialog = dialog;
    this.#openIntent = source;
    this.#interactionSource = interactionSource;
    this.#dialogHovered = false;
    this.#focusWithinActiveRegion =
      focusBeforeOpen instanceof Node && this.#isWithinActiveRegion(focusBeforeOpen);
    activeInstance = this;
    trigger.setAttribute('aria-expanded', 'true');

    const isSheet = this.#isSheet();
    dialog.dataset.displayMode = isSheet ? 'sheet' : 'popover';

    try {
      // A Theme Editor re-render can leave the target dialog open without
      // this instance owning it. Close that stale state before binding ours.
      if (dialog.open) dialog.close();
      this.#bindOpenListeners(dialog);

      if (isSheet) {
        dialog.setAttribute('scroll-lock', '');
        dialog.showModal();
        this.#syncScrollLock();
      } else {
        dialog.removeAttribute('scroll-lock');
        this.#suppressNativeDialogFocus = source === 'hover';
        try {
          dialog.show();
          if (source === 'hover') this.#restoreHoverFocus(dialog, focusBeforeOpen);
        } finally {
          this.#suppressNativeDialogFocus = false;
        }
      }
    } catch (error) {
      this.#clearDialogState(dialog, trigger, { restoreFocus: false });
      if (globalThis.Shopify?.designMode) {
        console.warn('[UlifRoomLookMosaic] unable to open product dialog', error);
      }
      return;
    }

    this.#observePanel(dialog);

    // The pointer can leave during the async close/open hand-off. Preserve the
    // full delay instead of leaving a hover-open panel orphaned.
    if (source === 'hover' && this.#hoveredHotspot !== trigger) {
      this.#scheduleHoverClose();
    }

    const raf = getRaf();
    raf(() => {
      if (this.#activeDialog !== dialog || !dialog.open) return;

      if (!isSheet) this.#positionPopover(trigger, dialog);
      if (focusPanel || isSheet) this.#focusDialog(dialog);
    });

    this.#publish('room_look_hotspot_open', trigger, { interactionSource });
  }

  /**
   * Close the active panel. Repeated calls share one promise.
   *
   * @param {{ restoreFocus?: boolean }} options
   * @returns {Promise<void>}
   */
  close(options = {}) {
    if (this.#closePromise) return this.#closePromise;

    this.#closePromise = this.#performClose(options).finally(() => {
      this.#closePromise = undefined;
    });
    return this.#closePromise;
  }

  async #performClose({ restoreFocus = true } = {}) {
    const dialog = this.#activeDialog;
    const trigger = this.#activeTrigger;

    if (!(dialog instanceof HTMLDialogElement)) {
      this.#abortOpenState();
      this.#activeTrigger = undefined;
      this.#activeDialog = undefined;
      this.#openIntent = 'closed';
      this.#interactionSource = undefined;
      if (activeInstance === this) activeInstance = undefined;
      this.#syncScrollLock();
      return;
    }

    const mode = dialog.dataset.displayMode;
    const shell = dialog.closest('dialog-component');
    this.#suppressNativeClose = true;
    this.#abortOpenState();

    try {
      if (dialog.open) {
        if (mode === 'sheet' && shell instanceof HTMLElement && typeof shell.closeDialog === 'function') {
          await shell.closeDialog();
        } else {
          dialog.close();
        }
      }
    } catch (error) {
      if (dialog.open) {
        try {
          dialog.close();
        } catch {
          // The element may have been removed during a section update.
        }
      }
      if (globalThis.Shopify?.designMode) {
        console.warn('[UlifRoomLookMosaic] unable to close product dialog', error);
      }
    } finally {
      this.#suppressNativeClose = false;
      this.#clearDialogState(dialog, trigger, { restoreFocus });
    }
  }

  #bindOpenListeners(dialog) {
    this.#openController?.abort();
    this.#openController = new AbortController();
    const signal = this.#openController.signal;

    document.addEventListener('pointerdown', this.#handleOutsidePointer, {
      capture: true,
      signal,
    });
    document.addEventListener('keydown', this.#handleDocumentKeydown, {
      capture: true,
      signal,
    });
    dialog.addEventListener('cancel', this.#handleDialogCancel, { signal });
    dialog.addEventListener('pointerdown', this.#handleDialogPointerDown, { signal });
    dialog.addEventListener('mouseenter', this.#handleDialogMouseEnter, { signal });
    dialog.addEventListener('mouseleave', this.#handleDialogMouseLeave, { signal });
    dialog.addEventListener('close', this.#handleNativeClose, { signal });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.#handleViewportResize, { signal });
    } else {
      window.addEventListener('resize', this.#handleViewportResize, { signal });
    }
  }

  #abortOpenState() {
    this.#cancelHoverClose();
    this.#dialogHovered = false;
    this.#focusWithinActiveRegion = false;
    this.#suppressNativeDialogFocus = false;
    this.#openController?.abort();
    this.#openController = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    if (this.#resizeFrame !== undefined) {
      window.cancelAnimationFrame?.(this.#resizeFrame);
      this.#resizeFrame = undefined;
    }
  }

  #observePanel(dialog) {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;

    if (typeof window.ResizeObserver !== 'function') return;

    this.#resizeObserver = new window.ResizeObserver(() => {
      if (this.#activeDialog !== dialog) return;
      this.#schedulePopoverPosition();
    });

    // Observe both dimensions that affect the anchored calculation.
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(dialog);
  }

  #schedulePopoverPosition() {
    if (this.#resizeFrame !== undefined) return;
    if (this.#activeDialog?.dataset.displayMode !== 'popover') return;

    const raf = getRaf();
    this.#resizeFrame = raf(() => {
      this.#resizeFrame = undefined;
      const dialog = this.#activeDialog;
      if (dialog?.dataset.displayMode === 'popover') {
        this.#positionPopover(this.#activeTrigger, dialog);
      }
    });
  }

  #positionPopover(trigger, dialog) {
    if (!(trigger instanceof HTMLElement) || dialog.dataset.displayMode !== 'popover') return;

    const tile = trigger.closest('.ulif-room-look__tile');
    if (!(tile instanceof HTMLElement)) return;

    const tileRect = tile.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const dialogRect = dialog.getBoundingClientRect();
    const viewportPadding = 16;
    const gap = 12;
    const panelWidth = Math.min(
      dialog.offsetWidth || dialogRect.width || 420,
      Math.max(1, window.innerWidth - viewportPadding * 2)
    );
    const panelHeight = Math.min(
      dialog.offsetHeight || dialogRect.height || 160,
      Math.max(1, window.innerHeight - viewportPadding * 2)
    );
    const preferredSide = dialog.dataset.panelSide === 'left' ? 'left' : 'right';
    const leftSpace = triggerRect.left - gap - viewportPadding;
    const rightSpace = window.innerWidth - viewportPadding - triggerRect.right - gap;
    const leftFits = leftSpace >= panelWidth;
    const rightFits = rightSpace >= panelWidth;
    let resolvedSide = preferredSide;

    if (preferredSide === 'left' && !leftFits && rightFits) {
      resolvedSide = 'right';
    } else if (preferredSide === 'right' && !rightFits && leftFits) {
      resolvedSide = 'left';
    }

    const preferredLeft =
      resolvedSide === 'left'
        ? triggerRect.left - gap - panelWidth
        : triggerRect.right + gap;
    const preferredTop = triggerRect.top + triggerRect.height / 2 - panelHeight / 2;
    const maxLeft = Math.max(viewportPadding, window.innerWidth - viewportPadding - panelWidth);
    const maxTop = Math.max(viewportPadding, window.innerHeight - viewportPadding - panelHeight);
    const left = Math.min(Math.max(preferredLeft, viewportPadding), maxLeft);
    const top = Math.min(Math.max(preferredTop, viewportPadding), maxTop);

    dialog.style.setProperty('--look-panel-left', left - tileRect.left + 'px');
    dialog.style.setProperty('--look-panel-top', top - tileRect.top + 'px');
    dialog.dataset.resolvedPanelSide = resolvedSide;
  }

  #isWithinActiveRegion(node) {
    return Boolean(this.#activeTrigger?.contains(node) || this.#activeDialog?.contains(node));
  }

  #bindHotspotHoverListeners(signal) {
    this.querySelectorAll('[data-room-look-hotspot]').forEach((hotspot) => {
      if (!(hotspot instanceof HTMLButtonElement) || this.#boundHotspots.has(hotspot)) return;

      this.#boundHotspots.add(hotspot);
      hotspot.addEventListener('mouseenter', this.#handleHotspotMouseEnter, { signal });
      hotspot.addEventListener('mouseleave', this.#handleHotspotMouseLeave, { signal });
    });
  }

  #scheduleHoverClose() {
    if (this.#openIntent !== 'hover') return;

    this.#cancelHoverClose();
    this.#hoverCloseTimer = window.setTimeout(() => {
      this.#hoverCloseTimer = undefined;
      if (!this.#connected || !this.#hoverMedia?.matches || this.#openIntent !== 'hover') return;

      const pointerInside =
        this.#hoveredHotspot === this.#activeTrigger || this.#dialogHovered;
      if (pointerInside || this.#focusWithinActiveRegion) return;

      void this.close({ restoreFocus: false });
    }, HOVER_CLOSE_DELAY);
  }

  #cancelHoverClose() {
    if (this.#hoverCloseTimer === undefined) return;
    window.clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = undefined;
  }

  #shouldRestoreFocus() {
    if (this.#openIntent !== 'hover') return true;

    const focused = document.activeElement;
    return focused instanceof Node && Boolean(this.#activeDialog?.contains(focused));
  }

  #getActivationSource(event) {
    if (event instanceof MouseEvent && event.detail === 0) return 'keyboard';
    if (event.sourceCapabilities?.firesTouchEvents) return 'touch';
    return 'click';
  }

  #focusDialog(dialog) {
    const focusable = dialog.querySelector(FOCUSABLE_SELECTOR);
    if (focusable instanceof HTMLElement) {
      focusable.focus({ preventScroll: true });
      return;
    }

    dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });
  }

  #restoreHoverFocus(dialog, previousFocus) {
    const nativeFocus = document.activeElement;
    if (!(nativeFocus instanceof HTMLElement) || !dialog.contains(nativeFocus)) return;

    if (
      previousFocus instanceof HTMLElement &&
      previousFocus !== document.body &&
      previousFocus.isConnected &&
      !dialog.contains(previousFocus)
    ) {
      previousFocus.focus({ preventScroll: true });
    }

    const remainingFocus = document.activeElement;
    if (remainingFocus instanceof HTMLElement && dialog.contains(remainingFocus)) {
      remainingFocus.blur();
    }
  }

  #trapFocus(event, dialog) {
    const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (element) => element instanceof HTMLElement && !element.hasAttribute('hidden')
    );
    if (focusable.length === 0) {
      event.preventDefault();
      this.#focusDialog(dialog);
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement;
    if (event.shiftKey && current === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  #clearDialogState(dialog, trigger, { restoreFocus }) {
    this.#abortOpenState();
    if (this.#hoveredHotspot === trigger) this.#hoveredHotspot = undefined;
    trigger?.setAttribute('aria-expanded', 'false');
    dialog.removeAttribute('scroll-lock');
    dialog.removeAttribute('data-display-mode');
    dialog.removeAttribute('data-resolved-panel-side');
    dialog.style.removeProperty('--look-panel-left');
    dialog.style.removeProperty('--look-panel-top');
    if (dialog.hasAttribute('tabindex')) dialog.removeAttribute('tabindex');

    this.#activeTrigger = undefined;
    this.#activeDialog = undefined;
    this.#openIntent = 'closed';
    this.#interactionSource = undefined;
    if (activeInstance === this) activeInstance = undefined;
    this.#syncScrollLock();

    if (restoreFocus && trigger?.isConnected) {
      trigger.focus({ preventScroll: true });
    }
  }

  #forceClose() {
    const dialog = this.#activeDialog;
    const trigger = this.#activeTrigger;
    this.#suppressNativeClose = true;
    this.#abortOpenState();

    if (dialog instanceof HTMLDialogElement) {
      try {
        if (dialog.open) dialog.close();
      } catch {
        // Ignore a dialog removed by Theme Editor before cleanup runs.
      }
      this.#clearDialogState(dialog, trigger, { restoreFocus: false });
    } else {
      this.#hoveredHotspot = undefined;
      this.#dialogHovered = false;
      this.#activeTrigger = undefined;
      this.#activeDialog = undefined;
      this.#openIntent = 'closed';
      this.#interactionSource = undefined;
      if (activeInstance === this) activeInstance = undefined;
      this.#syncScrollLock();
    }

    this.#suppressNativeClose = false;
  }

  #syncScrollLock() {
    const lockedElement = document.querySelector(
      'dialog[open][scroll-lock], details[open][scroll-lock]'
    );
    if (lockedElement) {
      document.documentElement.setAttribute('scroll-lock', '');
    } else {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  #isSheet() {
    return this.#sheetMedia?.matches ?? window.innerWidth <= 1199;
  }

  #unbindMediaQuery() {
    if (this.#legacyMediaListener && this.#sheetMedia?.removeListener) {
      this.#sheetMedia.removeListener(this.#handleDisplayModeChange);
    }
    if (this.#legacyHoverMediaListener && this.#hoverMedia?.removeListener) {
      this.#hoverMedia.removeListener(this.#handleDisplayModeChange);
    }
    this.#legacyMediaListener = false;
    this.#legacyHoverMediaListener = false;
    this.#sheetMedia = undefined;
    this.#hoverMedia = undefined;
  }

  #publish(name, node, { interactionSource } = {}) {
    const roomKey = node?.dataset.roomKey || node?.closest('[data-room-key]')?.dataset.roomKey;
    const payload = {
      section_id: this.dataset.sectionId || this.id || '',
      room_key: roomKey || '',
      template: 'index',
    };
    if (interactionSource) payload.interaction_source = interactionSource;

    const productHandle =
      node?.dataset.productHandle || node?.closest('[data-product-handle]')?.dataset.productHandle;
    const productEvent = name === 'room_look_hotspot_open' || name === 'room_look_product_click';
    if (productEvent) {
      if (!productHandle) return;
      payload.product_handle = productHandle;
    }

    this.dispatchEvent(
      new CustomEvent('ulif:room-look-analytics', {
        bubbles: true,
        detail: { name, payload },
      })
    );

    try {
      globalThis.Shopify?.analytics?.publish?.(name, payload);
    } catch (error) {
      if (globalThis.Shopify?.designMode) {
        console.warn('[UlifRoomLookMosaic] analytics publish failed', error);
      }
    }
  }
}

if (!customElements.get('ulif-room-look-mosaic')) {
  customElements.define('ulif-room-look-mosaic', UlifRoomLookMosaic);
}
